import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const PRESET_AMOUNTS = [1000, 2000, 5000, 10000];

const FREQUENCIES = [
  { key: 'once',         icon: 'bi-1-circle-fill',     yearly: (a) => a },
  { key: 'monthly',      icon: 'bi-arrow-repeat',       yearly: (a) => a * 12 },
  { key: 'twice_yearly', icon: 'bi-calendar2-range-fill',yearly: (a) => a * 2 },
  { key: 'yearly',       icon: 'bi-calendar-check-fill',yearly: (a) => a },
];

function formatYen(n) {
  return `¥${Number(n).toLocaleString('ja-JP')}`;
}

export default function DonationForm() {
  const { t, i18n } = useTranslation();

  const [name,      setName]      = useState('');
  const [email,     setEmail]     = useState('');
  const [amount,    setAmount]    = useState(10000);   // default ¥10,000
  const [customAmt, setCustomAmt] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [frequency, setFrequency] = useState('once'); // default one-time
  const [loading,   setLoading]   = useState(false);
  const [errors,    setErrors]    = useState({});

  const effectiveAmount = useCustom ? parseInt(customAmt || '0') : amount;
  const yearlyTotal = FREQUENCIES.find(f => f.key === frequency)?.yearly(effectiveAmount) || 0;

  function validate() {
    const e = {};
    if (!name.trim())  e.name = t('donation.requiredField');
    if (!email.trim()) e.email = t('donation.requiredField');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = t('donation.invalidEmail');
    if (effectiveAmount < 1000) e.amount = t('donation.minAmount');
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await axios.post('/api/donations/checkout', {
        name,
        email,
        amount:    effectiveAmount,
        frequency,
        locale:    i18n.language || 'ja',
      });
      window.location.href = data.url;
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'An error occurred' });
      setLoading(false);
    }
  }

  return (
    <section id="donate" className="py-5 py-lg-6">
      <div className="container">
        <div className="text-center mb-5">
          <div className="icon-circle icon-circle-primary mx-auto mb-3">
            <i className="bi bi-heart-fill" />
          </div>
          <h2 className="section-title">{t('donation.title')}</h2>
          <p className="text-muted fs-5">{t('donation.subtitle')}</p>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-7 col-xl-6">
            <div className="card border-0 shadow-lg rounded-4 p-4 p-lg-5">
              <form onSubmit={handleSubmit} noValidate>

                {/* Name */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-person-fill me-2" style={{ color: 'var(--primary)' }} />
                    {t('donation.nameLabel')}
                  </label>
                  <input
                    type="text"
                    className={`form-control form-control-lg rounded-3 ${errors.name ? 'is-invalid' : ''}`}
                    placeholder={t('donation.namePlaceholder')}
                    value={name}
                    onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })); }}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                {/* Email */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-envelope-fill me-2" style={{ color: 'var(--primary)' }} />
                    {t('donation.emailLabel')}
                  </label>
                  <input
                    type="email"
                    className={`form-control form-control-lg rounded-3 ${errors.email ? 'is-invalid' : ''}`}
                    placeholder={t('donation.emailPlaceholder')}
                    value={email}
                    onChange={e => { setEmail(e.target.value); setErrors(p => ({ ...p, email: '' })); }}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-currency-yen me-2" style={{ color: 'var(--primary)' }} />
                    {t('donation.amountLabel')}
                  </label>
                  <div className="row g-2 mb-3">
                    {PRESET_AMOUNTS.map(a => (
                      <div key={a} className="col-6 col-sm-3">
                        <button
                          type="button"
                          className={`amount-btn ${!useCustom && amount === a ? 'active' : ''}`}
                          onClick={() => { setAmount(a); setUseCustom(false); setErrors(p => ({ ...p, amount: '' })); }}
                        >
                          {a === 10000 && !useCustom && amount === 10000 && (
                            <div style={{ fontSize: '.6rem', letterSpacing: '.05em', opacity: .8, marginBottom: '2px' }}>
                              {t('donation.recommended')}
                            </div>
                          )}
                          {formatYen(a)}
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="input-group input-group-lg">
                    <span className="input-group-text bg-light fw-bold" style={{ color: 'var(--primary)', fontSize: '1.1rem' }}>¥</span>
                    <input
                      type="number"
                      min="1000"
                      className={`form-control rounded-end-3 ${useCustom ? 'border-primary' : ''} ${errors.amount ? 'is-invalid' : ''}`}
                      placeholder={t('donation.customPlaceholder')}
                      value={customAmt}
                      onFocus={() => setUseCustom(true)}
                      onChange={e => {
                        setCustomAmt(e.target.value);
                        setUseCustom(true);
                        setErrors(p => ({ ...p, amount: '' }));
                      }}
                    />
                    {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                  </div>
                  <small className="text-muted d-flex align-items-center gap-1 mt-1">
                    <i className="bi bi-info-circle" />{t('donation.customAmount')}
                  </small>
                </div>

                {/* Frequency */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-clock-history me-2" style={{ color: 'var(--primary)' }} />
                    {t('donation.frequencyLabel')}
                  </label>
                  <div className="row g-2">
                    {FREQUENCIES.map(f => (
                      <div key={f.key} className="col-6">
                        <button
                          type="button"
                          className={`freq-btn ${frequency === f.key ? 'active' : ''}`}
                          onClick={() => setFrequency(f.key)}
                        >
                          <i className={`bi ${f.icon} me-2`} />
                          {t(`donation.freq_${f.key}`)}
                        </button>
                      </div>
                    ))}
                  </div>

                  {effectiveAmount >= 1000 && frequency !== 'once' && (
                    <div className="mt-3 p-3 rounded-3 d-flex align-items-center gap-2"
                      style={{ background: 'rgba(253,126,20,.08)', border: '1px solid rgba(253,126,20,.2)' }}>
                      <i className="bi bi-calculator-fill fs-5" style={{ color: 'var(--secondary)' }} />
                      <span style={{ color: '#7c4000', fontWeight: 600 }}>
                        {t('donation.yearlyNote', { amount: formatYen(yearlyTotal) })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Summary */}
                {effectiveAmount >= 1000 && (
                  <div className="p-3 rounded-3 mb-4 d-flex align-items-center gap-3"
                    style={{ background: 'linear-gradient(135deg, rgba(214,51,132,.06), rgba(253,126,20,.06))', border: '1px solid rgba(214,51,132,.15)' }}>
                    <i className="bi bi-receipt fs-4" style={{ color: 'var(--primary)' }} />
                    <div className="flex-grow-1">
                      <div className="text-muted small">{t(`donation.freq_${frequency}`)}</div>
                      <div className="fw-bold fs-5" style={{ color: 'var(--primary)' }}>{formatYen(effectiveAmount)}</div>
                    </div>
                    <i className="bi bi-check-circle-fill fs-4" style={{ color: 'var(--accent)' }} />
                  </div>
                )}

                {errors.submit && (
                  <div className="alert alert-danger rounded-3 py-2 mb-3 d-flex align-items-center gap-2">
                    <i className="bi bi-exclamation-triangle-fill" />{errors.submit}
                  </div>
                )}

                <button type="submit" className="btn btn-donate w-100 fs-5" disabled={loading}>
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm me-2" />{t('donation.submitting')}</>
                  ) : (
                    <><i className="bi bi-lock-fill me-2" />{t('donation.submitBtn')}</>
                  )}
                </button>

                <div className="d-flex align-items-center justify-content-center gap-2 mt-3 text-muted small">
                  <i className="bi bi-shield-lock-fill" style={{ color: 'var(--accent)', fontSize: '1rem' }} />
                  {t('donation.secureNote')}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
